import { put, call, take, fork, takeLatest } from 'redux-saga/effects';
import { acceptError } from 'common/_redux';
import { REQUEST } from 'redux-config/reduxHelpers';

import * as api from 'common/dashboards/api';
import * as ActionTypes from './actions';

export function* getTargetSaga({ partnerId, sampleType, isClientInterface }) {
  try {
    let data;
    let status;
    if (sampleType === 'partner') {
      if (isClientInterface) {
        const response = yield call(api.getClientPartnerTarget);
        data = response.data;
        status = response.status;
      } else {
        const response = yield call(api.getPartnerTarget, partnerId);
        data = response.data;
        status = response.status;
      }
    }
    if (sampleType === 'full') {
      const response = yield call(api.getBrokerTarget);
      data = response.data;
      status = response.status;
    }
    if (sampleType === 'manager') {
      const response = yield call(api.getManagerTarget, partnerId);
      data = response.data;
      status = response.status;
    }

    if (status < 300) {
      yield put(ActionTypes.getTarget.success(data));
    } else {
      yield put(ActionTypes.getTarget.failure(data));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.getTarget.failure(e));
  }
}

export function* getTargetWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_TARGET[REQUEST]);
    yield call(getTargetSaga, data);
  }
}

export function* putTargetSaga({ target, sampleType, partnerId, isClientInterface }) {
  try {
    let data;
    let status;
    if (sampleType === 'partner') {
      if (isClientInterface) {
        const response = yield call(api.putClientPartnerTarget, target);
        data = response.data;
        status = response.status;
      } else {
        const response = yield call(api.putPartnerTarget, partnerId, target);
        data = response.data;
        status = response.status;
      }
    }
    if (sampleType === 'full') {
      const response = yield call(api.putBrokerTarget, target);
      data = response.data;
      status = response.status;
    }
    if (sampleType === 'manager') {
      const response = yield call(api.putManagerTarget, partnerId, target);
      data = response.data;
      status = response.status;
    }
    if (status < 300) {
      yield put(ActionTypes.putTarget.success(data));
      yield call(getTargetSaga, { sampleType, partnerId, isClientInterface });
    } else {
      yield put(ActionTypes.putTarget.failure(data));
    }
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.putTarget.failure(e));
  }
}

export function* putTargetWatcherSaga() {
  while (true) {
    const { payload } = yield take(ActionTypes.putTarget.REQUEST);
    yield call(putTargetSaga, payload);
  }
}

export function* getUsersHistorySaga({ hasBrokerAccess, partnerId, viewMode, brand }) {
  try {
    let response;
    if (hasBrokerAccess) {
      response = yield call(api.getBackofficeEvents, partnerId, viewMode, brand);
    } else {
      response = yield call(api.getPartnershipEvents);
    }
    yield put(ActionTypes.getUsersHistory.success(response.data));
  } catch (e) {
    if (e.response?.status !== 403) {
      yield put(acceptError(e));
      yield put(ActionTypes.getUsersHistory.failure(e));
    }
  }
}
export function* getUsersHistoryWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_USERS_HISTORY[REQUEST]);
    yield call(getUsersHistorySaga, data);
  }
}

export function* getTotalsSaga({ sampleType, accountableUsers, brand, isClientInterface }) {
  try {
    let response;
    if (isClientInterface) {
      response = yield call(api.getClientTotals, { sampleType, accountableUsers, brand });
    } else {
      response = yield call(api.getTotals, { sampleType, accountableUsers, brand });
    }
    yield put(ActionTypes.getTotals.success(response.data));
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.getTotals.failure(e));
  }
}
export function* getTotalsWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_TOTALS[REQUEST]);
    yield call(getTotalsSaga, data);
  }
}

export function* getBackofficeHistorySaga({ search }) {
  try {
    const { status, data } = yield call(api.getBackofficeHistory, search);
    if (status < 300) {
      yield put(ActionTypes.getBackofficeHistory.success(data));
    } else {
      yield put(ActionTypes.getBackofficeHistory.failure(data.detail));
    }
  } catch (e) {
    yield put(acceptError(e, true));
    yield put(ActionTypes.getBackofficeHistory.failure());
  }
}

export function* getBackofficeHistoryWatcherSaga() {
  yield takeLatest(ActionTypes.GET_BACKOFFICE_HISTORY[REQUEST], getBackofficeHistorySaga);
}

export function* getCurrentMonthFunnelsSaga({
  startDate,
  endDate,
  sampleType,
  accountableUsers,
  aggregations,
  brand,
  referType,
  isClientInterface,
}) {
  try {
    let response;
    if (isClientInterface) {
      response = yield call(
        api.getClientMonthFunnel,
        startDate,
        endDate,
        sampleType,
        accountableUsers,
        aggregations,
        brand,
        referType
      );
    } else {
      response = yield call(api.getMonthFunnel, startDate, endDate, sampleType, accountableUsers, aggregations, brand);
    }

    yield put(ActionTypes.getCurrentMonthFunnels.success(response.data));
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.getCurrentMonthFunnels.failure(e));
  }
}

export function* getCurrentMonthFunnelsWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_CURRENT_MONTH_FUNNELS[REQUEST]);
    yield call(getCurrentMonthFunnelsSaga, data);
  }
}

export function* getLastMonthFunnelsSaga({
  startDate,
  endDate,
  sampleType,
  accountableUsers,
  aggregations,
  brand,
  isClientInterface,
  referType,
}) {
  try {
    let response;
    if (isClientInterface) {
      response = yield call(
        api.getClientMonthFunnel,
        startDate,
        endDate,
        sampleType,
        accountableUsers,
        aggregations,
        brand,
        referType
      );
    } else {
      response = yield call(api.getMonthFunnel, startDate, endDate, sampleType, accountableUsers, aggregations, brand);
    }

    yield put(ActionTypes.getLastMonthFunnels.success(response.data));
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.getLastMonthFunnels.failure(e));
  }
}

export function* getLastMonthFunnelsWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_LAST_MONTH_FUNNELS[REQUEST]);
    yield call(getLastMonthFunnelsSaga, data);
  }
}

export function* getFirstMonthToCompareSaga({
  startDate,
  endDate,
  sampleType,
  accountableUsers,
  aggregations,
  brand,
  isClientInterface,
  referType,
}) {
  try {
    let response;
    if (isClientInterface) {
      response = yield call(
        api.getClientMonthFunnel,
        startDate,
        endDate,
        sampleType,
        accountableUsers,
        aggregations,
        brand,
        referType
      );
    } else {
      response = yield call(api.getMonthFunnel, startDate, endDate, sampleType, accountableUsers, aggregations, brand);
    }

    yield put(ActionTypes.getFirstMonthToCompare.success(response.data));
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.getFirstMonthToCompare.failure(e));
  }
}

export function* getFirstMonthToCompareWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_FIRST_MONTH_TO_COMPARE[REQUEST]);
    yield call(getFirstMonthToCompareSaga, data);
  }
}

export function* getSecondMonthToCompareSaga({
  startDate,
  endDate,
  sampleType,
  accountableUsers,
  aggregations,
  brand,
  isClientInterface,
  referType,
}) {
  try {
    let response;
    if (isClientInterface) {
      response = yield call(
        api.getClientMonthFunnel,
        startDate,
        endDate,
        sampleType,
        accountableUsers,
        aggregations,
        brand,
        referType
      );
    } else {
      response = yield call(api.getMonthFunnel, startDate, endDate, sampleType, accountableUsers, aggregations, brand);
    }

    yield put(ActionTypes.getSecondMonthToCompare.success(response.data));
  } catch (e) {
    yield put(acceptError(e));
    yield put(ActionTypes.getSecondMonthToCompare.failure(e));
  }
}

export function* getSecondMonthToCompareWatcherSaga() {
  while (true) {
    const data = yield take(ActionTypes.GET_SECOND_MONTH_TO_COMPARE[REQUEST]);
    yield call(getSecondMonthToCompareSaga, data);
  }
}

export default [
  fork(getCurrentMonthFunnelsWatcherSaga),
  fork(getLastMonthFunnelsWatcherSaga),
  fork(getFirstMonthToCompareWatcherSaga),
  fork(getSecondMonthToCompareWatcherSaga),
  fork(getTargetWatcherSaga),
  fork(putTargetWatcherSaga),
  fork(getUsersHistoryWatcherSaga),
  fork(getTotalsWatcherSaga),
  fork(getBackofficeHistoryWatcherSaga),
];
